import { useTranslation } from 'react-i18next';

import type { CardPhase, OutputFormat } from '../../../api/cards';
import type { CardResult } from '../../../types';
import { ResultsTable } from './ResultsTable';

/**
 * The finished batch: what came out, whether duplicate recipients were folded
 * together, and the archive itself.
 */
export function DownloadStep({
  results,
  jobId,
  phase,
  outputFormat,
  deduplicated,
  onDownload,
  onNewBatch,
}: {
  results: CardResult[];
  jobId: string | null;
  phase: CardPhase;
  outputFormat: OutputFormat;
  /** The batch had the same recipient more than once; only one card was made. */
  deduplicated: boolean;
  onDownload: () => void;
  onNewBatch: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {deduplicated ? (
        <div className="flex items-center gap-2 border border-coral/40 bg-coral/6 px-3 py-2 text-sm text-coral">
          <svg
            className="size-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"
            />
          </svg>
          {t('cardGenerator.deduplicated')}
        </div>
      ) : (
        <div className="flex items-center gap-2 border border-green/40 bg-green/12 px-3 py-2 text-sm text-green-ink">
          <svg
            className="size-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {t('cardGenerator.done', {
            count: results.filter((r) => r.status === 'success').length,
          })}
        </div>
      )}

      <ResultsTable results={results} jobId={jobId} phase={phase} />

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => {
            onDownload();
          }}
          className="btn-primary flex-1 justify-center"
        >
          {t('cardGenerator.download', {
            format: outputFormat.toUpperCase(),
          })}
        </button>
        <button type="button" onClick={onNewBatch} className="btn-ghost">
          {t('cardGenerator.newBatch')}
        </button>
      </div>
    </div>
  );
}
